<!--@format-->
<template>
    <div
        ref="searchWrapper"
        class="gst-search-hotels d-flex"
        tabindex="0"
        @click="throttleTooltipWarningSearchClose"
        @keypress.enter="throttleTooltipWarningSearchClose">
        <LocationSearchStoreDropDown
            ref="location"
            :value="location"
            class="flex-grow-1"
            no-border
            :location-to-prepopulate="locationToPrepopulate"
            :nudge-bottom="15"
            @input="onLocationChangeDo" />
        <BaseTooltip
            v-model="tooltipWarningSearch.show"
            top
            left
            absolute
            prevent-click-outside
            :position-x="tooltipWarningSearch.positionX"
            :position-y="tooltipWarningSearch.positionY"
            :text="$t('messages.searchWarning')"
            class-content="gst-search-hotels__tooltip-warning-search">
            <span></span>
        </BaseTooltip>
        <SearchDateDropdown
            ref="searchDate"
            v-model="date"
            :min="minBookingStartDate"
            :allow-same-day-range="false"
            color="#0860c4"
            calendar-color="#0860c4"
            class="gst-search-hotels__dates"
            :show-new-datepicker="true"
            @tab-keydown="onSearchDateDropdownTabKeyDownDo" />
        <SearchButton
            class="gst-search-hotels__submit"
            type="submit"
            @click.native="submit" />
    </div>
</template>
<script>
import { mapGetters } from 'vuex';
import throttle from 'lodash/throttle';
import SearchButton from '@core/shared/components/buttons/SearchButton.vue';
import SearchDateDropdown from '@core/shared/components/search/SearchDateDropdown.vue';
import LocationSearchStoreDropDown from '@tenant/app/components/misc/LocationSearchStoreDropDown/LocationSearchStoreDropDown';
import { formatQueryParam as dateUtilsFormatQueryParam } from '@tenant/app/utils/dateUtils';
import { minCheckInDate, defaultCheckOutDate } from '@tenant/app/utils/bookingUtils';
import { addDays } from '@tenant/app/utils/dateUtils';
import { buildHotelsRouteObject } from '@tenant/app/utils/searchUtils';

export default {
    name: 'SearchHotelsInput',
    components: {
        SearchButton,
        SearchDateDropdown,
        LocationSearchStoreDropDown,
    },
    i18nOptions: {
        namespaces: 'main',
        keyPrefix: 'views.home.searchProducts.hotels.search',
    },
    props: {
        locationToPrepopulate: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            location: {},
            date: {
                startDate: null,
                endDate: null,
            },
            tooltipWarningSearch: {
                show: false,
                positionX: 0,
                positionY: 0,
            },
        };
    },
    computed: {
        ...mapGetters({
            tokenStatus: `appState/tokenStatus`,
        }),
        minBookingStartDate() {
            return minCheckInDate(null, this.tokenStatus);
        },
    },
    watch: {
        date: {
            handler: function ({ startDate, endDate }) {
                if (startDate && !endDate) {
                    this.date.endDate = addDays(startDate, 1);
                }
            },
            deep: true,
        },
    },
    methods: {
        onLocationChangeDo(newLocation) {
            this.location = { ...newLocation };
        },
        submit(event) {
            event.preventDefault();
            event.stopPropagation();

            if (!this.location?.name) {
                const pos = this.$refs.searchWrapper.getBoundingClientRect();

                this.tooltipWarningSearch.positionX = pos.x + 100;
                this.tooltipWarningSearch.positionY = pos.y + pos.height + 75;
                this.tooltipWarningSearch.show = true;
            } else {
                const routeObject = buildHotelsRouteObject();
                const queryParams = { ...this.location };

                if (this.date.startDate) {
                    queryParams.startDate = dateUtilsFormatQueryParam(this.date.startDate);
                }
                if (this.date.endDate) {
                    queryParams.endDate = dateUtilsFormatQueryParam(this.date.endDate);
                }
                if (!this.date.endDate) {
                    const checkOutDate = defaultCheckOutDate(
                        this.minBookingStartDate,
                        this.location?.city,
                        this.location?.stateCode,
                        this.location?.countryCode
                    );
                    queryParams.startDate = dateUtilsFormatQueryParam(this.minBookingStartDate);
                    queryParams.endDate = dateUtilsFormatQueryParam(checkOutDate);
                }

                routeObject.query = { ...routeObject.query, ...queryParams };
                const newRoute = this.$router.resolve(routeObject);
                window.open(newRoute.href, '_blank');
            }
        },
        throttleTooltipWarningSearchClose: throttle(function () {
            this.tooltipWarningSearch.show = false;
        }, 20),
        isFormSubmitOnEnter(event) {
            return event.offsetX <= 0 && event.offsetY <= 0;
        },
        onSearchDateDropdownTabKeyDownDo() {
            setTimeout(() => {
                this.$refs.searchDate.$refs.inputSearch.$refs.input.blur();
                this.$refs.searchDate.closeMenu();
            }, 50);
        },
    },
    mounted() {
        window.addEventListener('resize', this.throttleTooltipWarningSearchClose);
    },
    destroyed() {
        window.removeEventListener('resize', this.throttleTooltipWarningSearchClose);
    },
};
</script>
<style lang="scss">
@import '@scssVariables';
@import '@scssMixins';

.gst-search-hotels {
    height: 100%;
    width: 100%;
    gap: 24px;
}

.gst-search-hotels--overlay {
    position: relative;
    font-size: font-size('xxs');
    font-weight: font-weight('regular');
    z-index: $z-index-header + 2;
}

.gst-search-hotels__tooltip-warning-search.v-tooltip__content {
    padding: theme-spacing(4), theme-spacing(2) !important;
    background-color: theme-color('new-tertiary') !important;
    color: theme-color('white') !important;
    max-width: 300px !important;
    border-radius: border-radius('xxs') !important;
}

.gst-search-hotels__submit {
    height: auto !important;
    background-color: theme-color('primary') !important;
    color: theme-color('white') !important;
    text-transform: none !important;
}
</style>

<style lang="scss">
@import '@scssVariables';
@import '@scssMixins';

.gst-search-date__picker {
    margin-top: theme-spacing(4) !important;
    border-radius: border-radius('xxs') !important;

    .gst-vc-date-picker-container {
        .gst-all-dates.gst-btn.v-btn,
        .gst-this-weekend.gst-btn.v-btn {
            height: 46px !important;
            border: 1px solid theme-color('primary');
            font-size: font-size('s');
            font-weight: font-weight('large');
            border-radius: border-radius('xs');
        }

        .gst-btn--confirm.gst-btn.v-btn {
            padding: theme-spacing(3, 5);
            font-size: font-size('s');
            font-weight: font-weight('large');
            border-radius: border-radius('xs');
        }

        .gst-btn--reset.v-btn {
            font-weight: font-weight('bold');
        }

        .gst-vc-date-picker {
            .vc-day-content {
                font-weight: font-weight('large');
            }

            .vc-weekday {
                font-weight: font-weight('regular');
            }
        }
    }
}

.gst-search-hotels .gst-search-icon-svg-focused {
    .gst-svg-icon {
        fill: theme-color('primary');
    }
}
</style>
